import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Package, Search, MapPin, Truck, CheckCircle, Clock, AlertCircle, ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { trackOrder, getOrderTracking } from '../lib/api';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  confirmed: { label: 'Confirmed', color: '#10B981', icon: CheckCircle },
  pending: { label: 'Pending', color: '#F59E0B', icon: Clock },
  processing: { label: 'Processing', color: '#3B82F6', icon: Package },
  shipped: { label: 'Shipped', color: '#8B5CF6', icon: Truck },
  delivered: { label: 'Delivered', color: '#10B981', icon: CheckCircle },
  payment_failed: { label: 'Payment Failed', color: '#EF4444', icon: AlertCircle },
  cancelled: { label: 'Cancelled', color: '#EF4444', icon: AlertCircle },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: '#64748B', icon: Clock };
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium"
      style={{ background: `${cfg.color}15`, color: cfg.color }}
      data-testid="order-status-badge"
    >
      <Icon size={14} /> {cfg.label}
    </span>
  );
}

function TrackingTimeline({ fulfillments }) {
  if (!fulfillments || fulfillments.length === 0) {
    return (
      <div className="bg-slate-50 rounded-lg p-6 text-center" data-testid="no-tracking-info">
        <Truck className="mx-auto text-slate-400 mb-2" size={32} />
        <p className="text-slate-500 text-sm">Tracking information will appear here once your order ships.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tracking-info">
      {fulfillments.map((f, i) => (
        <div key={i} className="bg-white border rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Truck size={18} className="text-[#003366]" />
              <span className="font-semibold text-slate-900">
                {f.tracking_company || 'Shipment'} {fulfillments.length > 1 ? `#${i + 1}` : ''}
              </span>
            </div>
            <span
              className="text-xs font-medium px-2 py-1 rounded-full"
              style={{
                background: f.status === 'success' ? '#10B98115' : '#F59E0B15',
                color: f.status === 'success' ? '#10B981' : '#F59E0B',
              }}
            >
              {f.shipment_status || f.status || 'In Transit'}
            </span>
          </div>

          {f.tracking_number && (
            <div className="flex items-center gap-2 text-sm mb-2">
              <span className="text-slate-500">Tracking #:</span>
              <span className="font-mono font-medium text-slate-800" data-testid="tracking-number">{f.tracking_number}</span>
            </div>
          )}

          {f.tracking_url && (
            <a
              href={f.tracking_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-[#003366] hover:underline"
              data-testid="tracking-url-link"
            >
              Track on carrier site <ExternalLink size={14} />
            </a>
          )}

          {f.updated_at && (
            <p className="text-xs text-slate-400 mt-2">Last updated: {new Date(f.updated_at).toLocaleString()}</p>
          )}
        </div>
      ))}
    </div>
  );
}

export default function TrackOrder() {
  const [searchParams] = useSearchParams();
  const directOrderId = searchParams.get('order_id');

  const [email, setEmail] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Direct link: fetch by order_id param
  useEffect(() => {
    if (directOrderId) {
      setLoading(true);
      getOrderTracking(directOrderId)
        .then((res) => setResult(res.data))
        .catch(() => toast.error('Could not load order details.'))
        .finally(() => setLoading(false));
    }
  }, [directOrderId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !orderNumber) {
      toast.error('Please enter both your email and order number.');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await trackOrder({ email, order_id: orderNumber });
      setResult(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Order not found.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const order = result?.order;
  const fulfillments = result?.fulfillments || [];

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-8" data-testid="track-order-page">
      <div className="max-w-3xl mx-auto px-4">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-[#003366] mb-6 text-sm">
          <ArrowLeft size={16} /> Back to Store
        </Link>

        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Track Your Order</h1>
        <p className="text-slate-500 mb-8">Enter your email and order number to check your order status and shipping details.</p>

        {/* Lookup form — hidden when viewing via direct link with results */}
        {!directOrderId && (
          <form onSubmit={handleSubmit} className="bg-white rounded-lg border p-6 mb-8" data-testid="track-order-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label htmlFor="track-email">Email Address</Label>
                <Input
                  type="email"
                  id="track-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="mt-1"
                  data-testid="track-email-input"
                />
              </div>
              <div>
                <Label htmlFor="track-order-id">Order Number</Label>
                <Input
                  type="text"
                  id="track-order-id"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="e.g. 7A3B2C1D or full order ID"
                  className="mt-1"
                  data-testid="track-order-id-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#003366] hover:bg-[#002855] text-white"
              data-testid="track-order-submit-btn"
            >
              <Search size={16} className="mr-2" />
              {loading ? 'Searching...' : 'Track Order'}
            </Button>
          </form>
        )}

        {/* Results */}
        {order && (
          <div className="space-y-6" data-testid="track-order-results">
            {/* Order header */}
            <div className="bg-white rounded-lg border p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-900" data-testid="order-id-display">
                    Order #{order.id.slice(0, 8).toUpperCase()}
                  </h2>
                  <p className="text-sm text-slate-500">
                    Placed on {new Date(order.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
                <StatusBadge status={order.status} />
              </div>

              {/* Shipping address */}
              {order.shipping_address && (
                <div className="flex items-start gap-2 text-sm text-slate-600">
                  <MapPin size={16} className="mt-0.5 text-slate-400 flex-shrink-0" />
                  <span>
                    {order.shipping_address.address}, {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zipCode}
                  </span>
                </div>
              )}
            </div>

            {/* Tracking / Fulfillment */}
            <div>
              <h3 className="text-base font-semibold text-slate-900 mb-3">Shipping & Tracking</h3>
              <TrackingTimeline fulfillments={fulfillments} />
            </div>

            {/* Order items */}
            <div className="bg-white rounded-lg border p-6">
              <h3 className="text-base font-semibold text-slate-900 mb-4">Items Ordered</h3>
              <div className="space-y-3">
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    {item.image && (
                      <div className="w-14 h-14 rounded bg-slate-100 flex-shrink-0 overflow-hidden">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{item.name}</p>
                      <p className="text-xs text-slate-500">Qty: {item.quantity}</p>
                    </div>
                    <p className="text-sm font-medium text-slate-900">${(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>

              <hr className="my-4" />
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>${order.subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Tax</span><span>${order.tax.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span>{order.shipping === 0 ? 'FREE' : `$${order.shipping.toFixed(2)}`}</span></div>
                <div className="flex justify-between font-bold text-base pt-2 border-t">
                  <span>Total</span><span className="text-[#003366]">${order.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Help */}
            <div className="text-center text-sm text-slate-500">
              Need help? Contact us at{' '}
              <a href="mailto:support@ipvaluemanagements.com" className="text-[#003366] hover:underline">
                support@ipvaluemanagements.com
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
